import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import {
  useLogisticsPermissions,
  type LogisticsPermissions,
} from "../hooks/useLogisticsPermissions";
import { TreasuryAccount } from "../types/treasury.types";

const { Text } = Typography;
const { Option } = Select;

const accountTypeLabels: Record<string, string> = {
  cash: "Caisse",
  bank: "Banque",
  mobile: "Mobile",
};

const moduleScopeLabels: Record<string, string> = {
  logistics: "Logistique",
  commercial: "Commercial",
};

const formatCurrency = (value: number | string, symbol?: string | null) => {
  const numericValue =
    typeof value === "string" ? parseFloat(value) : Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  const formatted = new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
  return symbol ? `${formatted} ${symbol}` : formatted;
};

type LogisticsTreasuryAccountsProps = {
  refreshKey?: number;
  permissions?: LogisticsPermissions;
  moduleScope?: "logistics" | "commercial" | "all";
};

const LogisticsTreasuryAccounts: React.FC<LogisticsTreasuryAccountsProps> = ({
  refreshKey,
  permissions: permissionsOverride,
  moduleScope: moduleScopeOverride,
}) => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const fallbackPermissions = useLogisticsPermissions();
  const permissions = permissionsOverride ?? fallbackPermissions;
  const moduleScope = moduleScopeOverride ?? "logistics";

  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingAccount, setEditingAccount] = useState<TreasuryAccount | null>(
    null
  );

  const [form] = Form.useForm();

  const canManageAccounts =
    permissions.canCreateTreasuryAccounts ||
    permissions.canEditTreasuryAccounts ||
    permissions.canDeleteTreasuryAccounts;

  const fetchAccounts = useCallback(async () => {
    if (!selectedCompany || !selectedWarehouse) {
      setAccounts([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_CONFIG.API_URL}/treasury/accounts`,
        {
          params: {
            company_id: selectedCompany,
            warehouse_id: selectedWarehouse,
            include_inactive: includeInactive,
            module_scope: moduleScope,
          },
        }
      );

      if (response.data?.success) {
        setAccounts(response.data.data || []);
      } else {
        message.error(
          response.data?.message || "Erreur lors du chargement des comptes"
        );
      }
    } catch (error: any) {
      console.error("[TREASURY] Error fetching accounts:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des comptes"
      );
    } finally {
      setLoading(false);
    }
  }, [includeInactive, selectedCompany, selectedWarehouse, moduleScope]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    if (refreshKey !== undefined) {
      fetchAccounts();
    }
  }, [fetchAccounts, refreshKey]);

  const openCreateModal = () => {
    setEditingAccount(null);
    form.resetFields();
    form.setFieldsValue({
      account_type: "cash",
      opening_balance: 0,
      is_default: false,
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEditModal = (account: TreasuryAccount) => {
    setEditingAccount(account);
    form.resetFields();
    form.setFieldsValue({
      name: account.name,
      account_type: account.account_type,
      opening_balance: account.opening_balance,
      opening_date: account.opening_date
        ? dayjs(account.opening_date)
        : null,
      is_default: account.is_default === 1,
      is_active: account.is_active === 1,
      notes: account.notes || "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedCompany || !selectedWarehouse) {
      message.warning("Selectionnez une entreprise et un établissement.");
      return;
    }

    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
        module_scope: moduleScope,
        name: values.name,
        account_type: values.account_type,
        opening_balance: values.opening_balance || 0,
        opening_date: values.opening_date
          ? values.opening_date.format("YYYY-MM-DD")
          : null,
        is_default: values.is_default || false,
        is_active: values.is_active ?? true,
        notes: values.notes || null,
      };

      if (editingAccount) {
        await axios.put(
          `${API_CONFIG.API_URL}/treasury/accounts/${editingAccount.id}`,
          payload
        );
        message.success("Compte mis a jour.");
      } else {
        await axios.post(`${API_CONFIG.API_URL}/treasury/accounts`, payload);
        message.success("Compte cree.");
      }

      setModalOpen(false);
      setEditingAccount(null);
      fetchAccounts();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error("[TREASURY] Error saving account:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de l'enregistrement du compte"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (accountId: number) => {
    try {
      await axios.delete(
        `${API_CONFIG.API_URL}/treasury/accounts/${accountId}`
      );
      message.success("Compte desactive.");
      fetchAccounts();
    } catch (error: any) {
      console.error("[TREASURY] Error deactivating account:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la desactivation du compte"
      );
    }
  };

  const columns = useMemo(
    () => {
      const cols: any[] = [
        {
          title: "Compte",
          dataIndex: "name",
          key: "name",
          render: (value: string, record: TreasuryAccount) => (
            <Space direction="vertical" size={0}>
              <Text>{value}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {accountTypeLabels[record.account_type] || record.account_type}
              </Text>
            </Space>
          ),
        },
        {
          title: "Module",
          dataIndex: "module_scope",
          key: "module_scope",
          render: (value: string) => moduleScopeLabels[value] || value || "-",
        },
        {
          title: "Solde ouverture",
          dataIndex: "opening_balance",
          key: "opening_balance",
          align: "right" as const,
          render: (value: number, record: TreasuryAccount) =>
            formatCurrency(value, record.currency_symbol),
        },
        {
          title: "Solde actuel",
          dataIndex: "current_balance",
          key: "current_balance",
          align: "right" as const,
          render: (value: number | null | undefined, record: TreasuryAccount) => {
            const balance =
              value !== undefined && value !== null
                ? value
                : record.opening_balance;
            return formatCurrency(balance, record.currency_symbol);
          },
        },
        {
          title: "Date ouverture",
          dataIndex: "opening_date",
          key: "opening_date",
          render: (value: string) =>
            value ? dayjs(value).format("DD/MM/YYYY") : "-",
        },
        {
          title: "Statut",
          dataIndex: "is_active",
          key: "is_active",
          render: (value: number) =>
            value ? <Tag color="green">Actif</Tag> : <Tag>Inactif</Tag>,
        },
        {
          title: "Defaut",
          dataIndex: "is_default",
          key: "is_default",
          render: (value: number) =>
            value ? <Tag color="blue">Oui</Tag> : <Tag>Non</Tag>,
        },
        {
          title: "Actions",
          key: "actions",
          align: "right" as const,
          render: (_: unknown, record: TreasuryAccount) => (
            <Space>
              <Button
                size="small"
                onClick={() => openEditModal(record)}
                disabled={!permissions.canEditTreasuryAccounts}
              >
                Modifier
              </Button>
              <Button
                size="small"
                danger
                onClick={() => handleDeactivate(record.id)}
                disabled={!permissions.canDeleteTreasuryAccounts}
              >
                Desactiver
              </Button>
            </Space>
          ),
        },
      ];

      if (moduleScope !== "all") {
        cols.splice(1, 1);
      }

      return cols;
    },
    [
      permissions.canDeleteTreasuryAccounts,
      permissions.canEditTreasuryAccounts,
      moduleScope,
    ]
  );

  if (!selectedCompany || !selectedWarehouse) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise et un établissement pour afficher les comptes."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div>
      <Card className="mb-4">
        <Space wrap style={{ justifyContent: "space-between", width: "100%" }}>
          <Space>
            <Checkbox
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
            >
              Afficher inactifs
            </Checkbox>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            disabled={!permissions.canCreateTreasuryAccounts}
          >
            Nouveau compte
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={accounts}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: "max-content" }}
        />
        <Text type="secondary" className="mt-2 block">
          Comptes: {accounts.length}.
        </Text>
      </Card>

      <Modal
        title={editingAccount ? "Modifier le compte" : "Nouveau compte"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText={editingAccount ? "Mettre a jour" : "Creer"}
        destroyOnClose
      >
        {!canManageAccounts && (
          <Alert
            message="Permissions insuffisantes"
            description="Vous n'avez pas les permissions pour modifier les comptes."
            type="warning"
            showIcon
            className="mb-3"
          />
        )}
        <Form form={form} layout="vertical" disabled={!canManageAccounts}>
          <Form.Item
            name="name"
            label="Nom du compte"
            rules={[{ required: true, message: "Nom requis" }]}
          >
            <Input placeholder="Ex: Caisse principale" />
          </Form.Item>
          <Form.Item
            name="account_type"
            label="Type"
            rules={[{ required: true, message: "Type requis" }]}
          >
            <Select>
              <Option value="cash">Caisse</Option>
              <Option value="bank">Banque</Option>
              <Option value="mobile">Mobile</Option>
            </Select>
          </Form.Item>
          <Form.Item name="opening_balance" label="Solde ouverture">
            <InputNumber style={{ width: "100%" }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="opening_date" label="Date ouverture">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="is_default" valuePropName="checked">
            <Checkbox>Compte par defaut pour ce type</Checkbox>
          </Form.Item>
          <Form.Item name="is_active" valuePropName="checked">
            <Checkbox>Compte actif</Checkbox>
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LogisticsTreasuryAccounts;
